import {
  SparkCreatorVideoDto,
  SparkLibraryStatus,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { SparkLibraryFilter } from '../../spark-ads.types';

/**
 * The single chip a library card shows.
 *
 * Mirrors the API's own derivation so the counts it returns and the chips rendered
 * here cannot disagree: sparked wins, then moderation, then authorisation.
 */
export function libraryStatusFor(video: SparkCreatorVideoDto): SparkLibraryStatus {
  if (video.isSparked) {
    return 'sparked';
  }
  if (video.moderation.status === 'in_moderation' || video.moderation.status === 'declined') {
    return 'inModeration';
  }
  if (video.auth.state === 'authorised') {
    return 'cleared';
  }
  return 'awaitingAuth';
}

export function matchesFilter(video: SparkCreatorVideoDto, filter: SparkLibraryFilter): boolean {
  return filter === 'all' || libraryStatusFor(video) === filter;
}

const STATUS_LABELS: Record<SparkLibraryStatus, string> = {
  sparked: 'Sparked',
  cleared: 'Cleared',
  awaitingAuth: 'Awaiting auth',
  inModeration: 'In moderation',
};

export function libraryStatusLabel(status: SparkLibraryStatus): string {
  return STATUS_LABELS[status];
}

/**
 * Chip styling per status. Uses the shared `.pill-*` classes so these read as the
 * same status vocabulary used elsewhere in the app.
 */
const STATUS_PILL_CLASSES: Record<SparkLibraryStatus, string> = {
  sparked: 'pill pill-purple',
  cleared: 'pill pill-green',
  awaitingAuth: 'pill pill-amber',
  inModeration: 'pill pill-gray',
};

export function libraryStatusPillClass(status: SparkLibraryStatus): string {
  return STATUS_PILL_CLASSES[status];
}

/** Sorts by organic engagement rate, strongest first. */
export function byEngagementRate(a: SparkCreatorVideoDto, b: SparkCreatorVideoDto): number {
  return (b.video.engagement_rate ?? 0) - (a.video.engagement_rate ?? 0);
}

/**
 * Formats a TikTok `create_time` into something readable.
 *
 * TikTok sends either a unix seconds value or a `YYYY-MM-DD HH:mm:ss` string, and
 * both need normalising before `Date` will parse them consistently.
 */
export function formatPostedAt(createTime: string | number | null | undefined): string {
  if (createTime === null || createTime === undefined || createTime === '') {
    return 'Unknown date';
  }

  const asNumber = typeof createTime === 'number' ? createTime : Number(createTime);
  const parsed = Number.isFinite(asNumber)
    ? new Date(asNumber * 1000)
    : new Date(String(createTime).replace(' ', 'T') + 'Z');

  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown date';
  }

  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
